import {
  setFocus,
  getCurrentFocusKey,
} from "@noriginmedia/norigin-spatial-navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import { useLocation } from "react-router"

import { focusStore } from "../store/FocusStore"

export type AddFocusFunctionProps = {
  forceKey?: string
  meta?: unknown
}
export type AddFocusFunction = (props?: AddFocusFunctionProps) => void
export type GetFocusFunction = (query?: unknown) => undefined | string | false
export type GetMetaInfo<T> = () => false | NonNullable<T> | undefined

export const useFocusHelper = () => {
  const location = useLocation()
  const focusKey = useRef<string | undefined>(null)
  const findFocus = focusStore((state) => state.findFocus)
  const findMeta = focusStore((state) => state.findMeta)
  const addFocusStore = focusStore((state) => state.addFocus)
  const prepSetKey = useRef(true)
  const [myRoute] = useState(location.pathname + location.key)

  // when initialized, the hook saves its route and when leaving it attempts to store its last used key on the route.
  useEffect(() => {
    if (
      location.pathname + location.key !== myRoute &&
      !focusKey.current &&
      prepSetKey.current
    ) {
      focusKey.current = getCurrentFocusKey()
      prepSetKey.current = false
    }
    if (location.pathname === myRoute) prepSetKey.current = true
  }, [location])

  /* try to set the focus on the last saved focus key, otherwise look for it in the store if the pages has been unset
   in the meantime */
  const getFocus: GetFocusFunction = useCallback(
    (query?: unknown) => {
      const currentKey = getCurrentFocusKey()
      if (location.pathname + location.key === myRoute) {
        const key = findFocus({ route: location.pathname + location.key })
        const ownKey = focusKey.current

        if (currentKey !== getCurrentFocusKey())
          // user already changed focus
          return
        if (key) {
          setFocus(key, query ?? { instantScroll: true })
          focusKey.current = undefined
        } else if (focusKey.current) {
          setFocus(focusKey.current, query ?? { instantScroll: true })
          focusKey.current = undefined
        }

        if (key) return key
        if (ownKey) return ownKey
        return false
      }
    },
    [location],
  )

  const getMetaInfo = useCallback(<T,>() => {
    if (location.pathname + location.key === myRoute) {
      const meta = findMeta({ route: location.pathname + location.key })
      return (meta as T) ?? false
    }
  }, [location])

  const addFocus: AddFocusFunction = useCallback(
    (props) => {
      addFocusStore({
        route: myRoute,
        key: props?.forceKey ?? getCurrentFocusKey(),
        meta: props?.meta ?? undefined,
      })
    },
    [myRoute],
  )

  return { addFocus, getFocus, getMetaInfo }
}
